import React, { useState, useEffect } from "react";
import logo from "../assets/logo_nobg.png";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";

function fmtDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function readTime(blocks) {
  const text = (blocks || []).map((b) => b.content || "").join(" ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function Finance() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [communityCount, setCommunityCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      setBlogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((b) => (b.status || "published") === "published"));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    getCountFromServer(collection(db, "communities")).then((s) => setCommunityCount(s.data().count)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [featured, ...rest] = blogs;

  return (
    <div className="fn">
      <style>{CSS}</style>

      <nav className={`fn-nav${scrolled ? " fn-nav--solid" : ""}`}>
        <div className="fn-nav-inner">
          <a href="https://shashankupadhyaya.com" className="fn-logo">
            <img src={logo} alt="S/U" className="site-logo-img" />
            <span className="fn-logo-label">Finance &amp; Markets</span>
          </a>
          <div className="fn-nav-links">
            <button className="fn-nav-link" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Articles</button>
            <button className="fn-nav-link" onClick={() => navigate("/community")}>Community</button>
          </div>
          <a href="https://shashankupadhyaya.com" className="fn-nav-back">← Back to portfolio</a>
        </div>
      </nav>

      <div className="fn-page">

        {/* Page header */}
        <div className="fn-header">
          <div className="fn-header-inner">
            <span className="fn-chip">Finance &amp; Markets</span>
            <h1 className="fn-h1">Market Insights<br />&amp; Analysis</h1>
            <p className="fn-lead">Independent analysis on markets, investing, and wealth building. No noise, just signal.</p>
            <div className="fn-stats-row">
              <div className="fn-stat"><span className="fn-stat-val">{blogs.length}</span><span className="fn-stat-label">Articles</span></div>
              <div className="fn-stat-div" />
              <div className="fn-stat"><span className="fn-stat-val">{communityCount}</span><span className="fn-stat-label">Communities</span></div>
            </div>
          </div>
        </div>

        <div className="fn-container">

          {loading ? (
            <div className="fn-state">
              <div className="fn-spinner" />
              <span>Loading articles…</span>
            </div>
          ) : blogs.length === 0 ? (
            <div className="fn-state fn-state--empty">
              <span className="fn-state-icon">📝</span>
              <p>No articles yet. Check back soon.</p>
            </div>
          ) : (
            <>
              {/* Featured post */}
              {featured && (
                <div className="fn-section">
                  <h2 className="fn-section-title">Latest Article</h2>
                  <div className="fn-featured" onClick={() => navigate(`/blog/${featured.id}`)}>
                    <div className="fn-featured-media">
                      {featured.thumbnail
                        ? <img src={featured.thumbnail} alt={featured.title} />
                        : <div className="fn-featured-placeholder">◈</div>}
                    </div>
                    <div className="fn-featured-body">
                      <div className="fn-featured-meta">
                        <span className="fn-badge fn-badge--gold">Featured</span>
                        <span className="fn-meta-text">{fmtDate(featured.createdAt)}</span>
                        <span className="fn-meta-dot">·</span>
                        <span className="fn-meta-text">{readTime(featured.blocks)} min read</span>
                      </div>
                      <h2 className="fn-featured-title">{featured.title}</h2>
                      {featured.excerpt && <p className="fn-featured-excerpt">{featured.excerpt}</p>}
                      <button className="fn-btn fn-btn--primary" onClick={(e) => { e.stopPropagation(); navigate(`/blog/${featured.id}`); }}>
                        Read Article →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* More posts */}
              {rest.length > 0 && (
                <div className="fn-section">
                  <h2 className="fn-section-title">All Articles</h2>
                  <div className="fn-grid">
                    {rest.map((b) => (
                      <div key={b.id} className="fn-card" onClick={() => navigate(`/blog/${b.id}`)}>
                        <div className="fn-card-media">
                          {b.thumbnail
                            ? <img src={b.thumbnail} alt={b.title} />
                            : <div className="fn-card-placeholder">◈</div>}
                        </div>
                        <div className="fn-card-body">
                          <div className="fn-card-meta">
                            <span className="fn-meta-text">{fmtDate(b.createdAt)}</span>
                            <span className="fn-meta-dot">·</span>
                            <span className="fn-meta-text">{readTime(b.blocks)} min read</span>
                          </div>
                          <h3 className="fn-card-title">{b.title}</h3>
                          {b.excerpt && <p className="fn-card-excerpt">{b.excerpt}</p>}
                          <span className="fn-card-cta">Read more →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Community callout */}
          <div className="fn-community" onClick={() => navigate("/community")}>
            <div className="fn-community-icon">◈</div>
            <div className="fn-community-body">
              <h3 className="fn-community-title">Join the Finance Community</h3>
              <p className="fn-community-sub">
                {communityCount > 0 ? `${communityCount} active channel${communityCount !== 1 ? "s" : ""}` : "Active channels"} — markets, investing &amp; wealth discussions
              </p>
            </div>
            <button className="fn-btn fn-btn--outline">Explore →</button>
          </div>

        </div>

        <footer className="fn-footer">
          <div className="fn-container">
            <span>© 2026 Shashank Upadhyaya</span>
            <a href="https://shashankupadhyaya.com">shashankupadhyaya.com ↗</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

const CSS = `
  .fn {
    --bg: #0A0B0D;
    --card: #0E1116;
    --border: rgba(255,255,255,0.08);
    --text: #EDEFF2;
    --muted: #9AA3AF;
    --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12);
    --nav-h: 64px;
    --radius: 8px;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }
  .fn *, .fn *::before, .fn *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .fn a { color: inherit; text-decoration: none; }
  .fn button { cursor: pointer; font-family: inherit; }
  .fn img { display: block; width: 100%; object-fit: cover; }

  /* Nav */
  .fn-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: var(--nav-h);
    transition: background 0.25s, border-color 0.25s, backdrop-filter 0.25s;
    border-bottom: 1px solid transparent;
  }
  .fn-nav--solid {
    background: rgba(10,11,13,0.95);
    backdrop-filter: blur(20px);
    border-bottom-color: var(--border);
  }
  .fn-nav-inner {
    max-width: 1100px; margin: 0 auto; padding: 0 28px;
    height: 100%; display: flex; align-items: center; gap: 32px;
  }
  .fn-logo { display: flex; align-items: center; gap: 10px; }
  .site-logo-img { height: 56px; width: auto; display: block; object-fit: contain; }
  .fn-logo-label {
    font-size: 12px; color: var(--muted); padding-left: 10px;
    border-left: 1px solid var(--border);
  }
  .fn-nav-links { display: flex; gap: 4px; margin-left: auto; }
  .fn-nav-link {
    background: none; border: none; color: var(--muted); font-size: 14px;
    padding: 6px 14px; border-radius: 6px; transition: all 0.15s;
  }
  .fn-nav-link:hover { color: var(--text); background: var(--border); }
  .fn-nav-back {
    font-size: 13px; color: var(--muted); transition: color 0.15s;
    padding: 6px 0; white-space: nowrap;
  }
  .fn-nav-back:hover { color: var(--gold); }

  /* Page */
  .fn-page { padding-top: var(--nav-h); }

  /* Header */
  .fn-header {
    border-bottom: 1px solid var(--border);
    padding: 64px 0 48px;
  }
  .fn-header-inner {
    max-width: 1100px; margin: 0 auto; padding: 0 28px;
  }
  .fn-chip {
    display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--gold);
    background: var(--gold-dim); border: 1px solid rgba(199,249,75,0.25);
    padding: 4px 10px; border-radius: 4px; margin-bottom: 20px;
  }
  .fn-h1 {
    font-family: 'Archivo', sans-serif; font-weight: 900;
    font-size: clamp(32px, 5vw, 56px); letter-spacing: -0.03em;
    line-height: 1.05; margin-bottom: 16px;
  }
  .fn-lead { font-size: 16px; color: var(--muted); max-width: 480px; line-height: 1.7; margin-bottom: 28px; }
  .fn-stats-row { display: flex; align-items: center; gap: 20px; }
  .fn-stat { display: flex; flex-direction: column; gap: 2px; }
  .fn-stat-val { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 22px; }
  .fn-stat-label { font-size: 12px; color: var(--muted); }
  .fn-stat-div { width: 1px; height: 32px; background: var(--border); }

  /* Container */
  .fn-container { max-width: 1100px; margin: 0 auto; padding: 0 28px; }

  /* Section */
  .fn-section { padding: 48px 0; border-bottom: 1px solid var(--border); }
  .fn-section-title {
    font-family: 'Archivo', sans-serif; font-weight: 700;
    font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 24px;
  }

  /* Featured */
  .fn-featured {
    display: grid; grid-template-columns: 1fr 1fr; gap: 36px;
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden; cursor: pointer;
    transition: border-color 0.2s;
  }
  .fn-featured:hover { border-color: var(--gold); }
  .fn-featured-media { min-height: 280px; background: #2e2e2e; overflow: hidden; }
  .fn-featured-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .fn-featured:hover .fn-featured-media img { transform: scale(1.03); }
  .fn-featured-placeholder {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
    font-size: 48px; color: rgba(255,255,255,0.1); min-height: 280px;
  }
  .fn-featured-body { padding: 32px 32px 32px 0; display: flex; flex-direction: column; justify-content: center; gap: 14px; }
  .fn-featured-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .fn-featured-title {
    font-family: 'Archivo', sans-serif; font-weight: 800;
    font-size: clamp(20px, 2.5vw, 28px); letter-spacing: -0.02em; line-height: 1.2;
  }
  .fn-featured-excerpt { font-size: 14px; color: var(--muted); line-height: 1.7; }

  /* Grid */
  .fn-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .fn-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden; cursor: pointer;
    transition: border-color 0.2s, transform 0.2s;
    display: flex; flex-direction: column;
  }
  .fn-card:hover { border-color: var(--gold); transform: translateY(-2px); }
  .fn-card-media { height: 160px; background: #2e2e2e; overflow: hidden; }
  .fn-card-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .fn-card:hover .fn-card-media img { transform: scale(1.04); }
  .fn-card-placeholder {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
    font-size: 28px; color: rgba(255,255,255,0.1);
  }
  .fn-card-body { padding: 20px; flex: 1; display: flex; flex-direction: column; gap: 8px; }
  .fn-card-meta { display: flex; align-items: center; gap: 6px; }
  .fn-card-title {
    font-family: 'Archivo', sans-serif; font-weight: 700;
    font-size: 16px; letter-spacing: -0.01em; line-height: 1.3;
  }
  .fn-card-excerpt { font-size: 13px; color: var(--muted); line-height: 1.6; flex: 1; }
  .fn-card-cta { font-size: 13px; color: var(--gold); font-weight: 600; margin-top: 4px; }

  /* Meta */
  .fn-meta-text { font-size: 12px; color: var(--muted); }
  .fn-meta-dot { font-size: 12px; color: var(--border); }

  /* Badges */
  .fn-badge {
    display: inline-block; font-size: 10px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase;
    padding: 3px 8px; border-radius: 4px;
  }
  .fn-badge--gold { background: var(--gold-dim); color: var(--gold); border: 1px solid rgba(199,249,75,0.3); }

  /* Community callout */
  .fn-community {
    margin: 48px 0; padding: 28px 32px;
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); display: flex; align-items: center; gap: 20px;
    cursor: pointer; transition: border-color 0.2s;
  }
  .fn-community:hover { border-color: var(--gold); }
  .fn-community-icon { font-size: 28px; color: var(--gold); flex-shrink: 0; }
  .fn-community-body { flex: 1; }
  .fn-community-title { font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 17px; margin-bottom: 4px; }
  .fn-community-sub { font-size: 13px; color: var(--muted); }

  /* Buttons */
  .fn-btn {
    display: inline-flex; align-items: center; font-size: 14px; font-weight: 600;
    padding: 10px 20px; border-radius: 6px; border: none;
    cursor: pointer; transition: all 0.15s; gap: 6px;
  }
  .fn-btn--primary { background: var(--gold); color: #000; }
  .fn-btn--primary:hover { opacity: 0.88; }
  .fn-btn--outline {
    background: transparent; color: var(--text);
    border: 1px solid var(--border); flex-shrink: 0;
  }
  .fn-btn--outline:hover { border-color: var(--gold); color: var(--gold); }

  /* States */
  .fn-state {
    text-align: center; padding: 80px 0;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    font-size: 14px; color: var(--muted);
  }
  .fn-state--empty .fn-state-icon { font-size: 32px; }
  .fn-spinner {
    width: 28px; height: 28px; border-radius: 50%;
    border: 2px solid var(--border); border-top-color: var(--gold);
    animation: fn-spin 0.75s linear infinite;
  }
  @keyframes fn-spin { to { transform: rotate(360deg); } }

  /* Footer */
  .fn-footer {
    margin-top: 80px; border-top: 1px solid var(--border);
    padding: 28px 0;
  }
  .fn-footer .fn-container {
    display: flex; align-items: center; justify-content: space-between;
    font-size: 13px; color: var(--muted);
  }
  .fn-footer a { transition: color 0.15s; }
  .fn-footer a:hover { color: var(--gold); }

  /* Responsive */
  @media (max-width: 900px) {
    .fn-grid { grid-template-columns: repeat(2, 1fr); }
    .fn-featured { grid-template-columns: 1fr; }
    .fn-featured-body { padding: 24px; }
    .fn-featured-media { min-height: 200px; }
  }
  @media (max-width: 600px) {
    .fn-grid { grid-template-columns: 1fr; }
    .fn-header { padding: 40px 0 32px; }
    .fn-h1 { font-size: 28px; }
    .fn-nav-back { display: none; }
    .fn-nav-links { display: none; }
    .fn-logo-label { display: none; }
    .site-logo-img { height: 40px; }
    .fn-community { flex-direction: column; align-items: flex-start; padding: 20px 18px; }
    .fn-container, .fn-header-inner { padding-left: 16px; padding-right: 16px; }
  }
  @media (max-width: 480px) {
    .fn-featured-body { padding: 16px; }
    .fn-featured-media, .fn-featured-placeholder { min-height: 160px; }
    .fn-footer .fn-container { flex-direction: column; gap: 8px; text-align: center; }
  }
`;
